'use client';

import Link from 'next/link';
import { useState, type FormEvent } from 'react';

type FirstChapterLeadFormProps = {
  sourceSlug: string;
  sourceType?: 'homepage' | 'friend-page';
  sourceName: string;
  offer: string;
  redirectPath: string;
  submitLabel: string;
  available?: boolean;
};

export function FirstChapterLeadForm({
  sourceSlug,
  sourceType = 'homepage',
  sourceName,
  offer,
  redirectPath,
  submitLabel,
  available = true,
}: FirstChapterLeadFormProps) {
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [nextPath, setNextPath] = useState(redirectPath);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }

    try {
      setStatus('saving');
      setMessage(null);
      setNewsletterMessage('');
      setDownloadUrl(null);
      const response = await fetch('/api/marketing/leads', {
        method: 'POST',
        signal: AbortSignal.timeout(25_000),
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: 'requested_sample',
          newsletterOptIn,
          email: cleanEmail,
          firstName: firstName.trim(),
          sourceSlug,
          sourceType,
          sourceName,
          offer,
          redirectPath,
          accessCodeMethod: 'none',
          registeredAmazonPurchase: false,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Unable to send the chapter right now.');
      setStatus('saved');
      setDownloadUrl(typeof payload.downloadUrl === 'string' && payload.downloadUrl ? payload.downloadUrl : null);
      setNextPath(typeof payload.redirectPath === 'string' && payload.redirectPath ? payload.redirectPath : redirectPath);
      setMessage(payload.emailDelivery?.message || 'Request saved. Email status is unavailable; check again before requesting a resend.');
      setNewsletterMessage(payload.newsletter?.message || '');
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error && !['TimeoutError', 'AbortError', 'TypeError'].includes(error.name) ? error.message : 'We could not confirm the request status. Try again to check it.');
    }
  }

  return (
    <form aria-label="First chapter request" onSubmit={handleSubmit} className="grid gap-6 p-8 lg:grid-cols-[minmax(0,1fr)_240px] lg:p-10">
      {!available ? <p role="status" className="lg:col-span-2 text-sm">The first-chapter sample is currently unavailable. Please check back later.</p> : null}
      <div className="grid gap-5 lg:col-span-1">
        <label className="space-y-3 text-[15px] font-bold text-[#2f2d2a]">
          <span>First name</span>
          <input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            placeholder="Your first name"
            autoComplete="given-name"
            className="h-16 w-full rounded-[.38rem] border border-[#d9c9bd] bg-white px-5 text-[17px] font-normal text-[#2f2d2a] outline-none transition placeholder:text-[#9f9187] focus:border-[#5f8769] focus:ring-4 focus:ring-[#5f8769]/10"
          />
        </label>
        <label className="space-y-3 text-[15px] font-bold text-[#2f2d2a]">
          <span>Email address</span>
          <input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@email.com"
            type="email"
            autoComplete="email"
            required
            className="h-16 w-full rounded-[.38rem] border border-[#d9c9bd] bg-white px-5 text-[17px] font-normal text-[#2f2d2a] outline-none transition placeholder:text-[#9f9187] focus:border-[#5f8769] focus:ring-4 focus:ring-[#5f8769]/10"
          />
        </label>
        <label className="flex items-start gap-3 text-sm leading-6">
          <input type="checkbox" checked={newsletterOptIn} onChange={(event) => setNewsletterOptIn(event.target.checked)} className="mt-1 h-5 w-5 shrink-0" />
          <span>Optional: Email me the newsletter and book updates. I can unsubscribe at any time. A separate confirmation is required.</span>
        </label>
      </div>
      <div className="flex flex-col justify-center gap-4">
        <button
          type="submit"
          disabled={status === 'saving' || !available}
          className="h-14 rounded-[.42rem] bg-[#5f8769] px-6 text-[15px] font-semibold text-white shadow-[0_14px_34px_rgba(95,135,105,.18)] transition hover:bg-[#547a5f] disabled:cursor-not-allowed disabled:opacity-65"
        >
          {status === 'saving' ? 'Saving request...' : status === 'saved' ? 'Check email status' : submitLabel}
        </button>
        <p className="text-[13px] leading-6 text-[#6b6057]">
          Your email is used to deliver this sample. Newsletter signup is optional and does not affect access.
        </p>
      </div>
      {message ? (
        <div role="status" aria-live="polite" className={`lg:col-span-2 rounded-[.5rem] border px-4 py-3 text-[14px] leading-6 ${status === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-[#cfe0d3] bg-[#f2f8f3] text-[#385f43]'}`}>
          <p>{message}</p>
          {newsletterMessage ? <p className="mt-2">{newsletterMessage} <Link href="/newsletter" className="underline">Newsletter preferences</Link></p> : null}
          {downloadUrl || nextPath ? (
            <div className="mt-3 flex flex-wrap gap-3">
              {downloadUrl ? (
                <a href={downloadUrl} target="_blank" rel="noreferrer" className="inline-flex h-10 items-center rounded-[.35rem] bg-[#5f8769] px-4 text-[13px] font-semibold text-white">
                  Download sample now (separate from email)
                </a>
              ) : null}
              <a href={nextPath} className="inline-flex h-10 items-center rounded-[.35rem] border border-[#d9c9bd] bg-white px-4 text-[13px] font-semibold text-[#2f2d2a]">
                Continue to book options
              </a>
            </div>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
